import { loadPyodide, version as pyodide_version, type PyodideInterface } from 'pyodide';

interface PyodidePackageCache {
  pypiPackages: Set<string>;
  standardPackages: Set<string>;
}

interface PyodideExecutionContext {
  explorationId: string;
  cellId: string;
}

class PyodideService {
  private static instance: PyodideService | null = null;
  private pyodide: PyodideInterface | null = null;
  private loading = false;
  private exclusiveRunQueue: Promise<void> = Promise.resolve();
  private supersimpleModuleRegistered = false;
  private activeSupersimpleCellId: string | null = null;
  private supersimpleLoadersByCellId: Map<
    string,
    (
      title: string,
      page?: boolean,
      limit?: number,
      page_size?: number,
      columns?: string[],
    ) => Promise<any>
  > = new Map();
  private packageCache: PyodidePackageCache = {
    pypiPackages: new Set(),
    standardPackages: new Set(),
  };
  private loadingPromise: Promise<PyodideInterface> | null = null;
  private cellNamespaces: Map<string, any> = new Map();

  private constructor() {}

  private configureMatplotlib(pyodide: PyodideInterface): void {
    try {
      pyodide.runPython(`
try:
    import os
    os.environ['MPLBACKEND'] = 'Agg'
except Exception:
    pass

try:
    import matplotlib
    matplotlib.use('Agg', force=True)

    import matplotlib.pyplot as plt
    plt.ioff()
    plt.show = lambda *args, **kwargs: None
except ImportError:
    pass
except Exception:
    # Best-effort only
    pass
`);
    } catch {
      // Best-effort only; never fail execution due to configuration issues.
    }
  }

  private cleanupMatplotlib(pyodide: PyodideInterface): void {
    try {
      pyodide.runPython(`
try:
    import matplotlib.pyplot as plt
    plt.close('all')
    try:
        from matplotlib._pylab_helpers import Gcf
        Gcf.destroy_all()
    except Exception:
        pass
except Exception:
    pass
`);
    } catch {
      // Best-effort cleanup only; never fail execution due to cleanup issues.
    }
  }

  /**
   * Flushes any pending matplotlib figures from the Pyodide runtime and returns a summary.
   *
   * This method:
   * - Detects all currently open figures in the Python environment.
   * - If at least one figure exists, renders the most recently created/active figure to a PNG image,
   *   encodes it as a base64 data URL, and includes it in the result.
   * - Always closes and destroys all matplotlib figures after rendering, so subsequent calls will not
   *   see previously rendered figures.
   *
   * It is intended to be called after executing a Python cell that may have produced matplotlib output.
   * Calling it before any plotting has occurred will simply return a `figureCount` of 0 and no image.
   *
   * @returns An object describing the flushed figures:
   * - `figureCount`: the number of matplotlib figures that were open before flushing.
   * - `base64ImageString` (optional): a `data:image/png;base64,...` URL containing the rendered image
   *   of the last (most recent) figure, present only when at least one figure was available.
   */
  async flushMatplotlibFigures(): Promise<{ base64ImageString?: string; figureCount: number }> {
    const pyodide = await this.initializePyodide();

    try {
      const result = pyodide.runPython(`
import base64, io

_result = None

try:
    import matplotlib.pyplot as plt
except ImportError:
    _result = dict(figureCount=0)
else:
    fignums = plt.get_fignums()
    if not fignums:
        _result = dict(figureCount=0)
    else:
        try:
            plt.figure(fignums[-1])
            fig = plt.gcf()
            buf = io.BytesIO()
            fig.savefig(buf, format='png', bbox_inches='tight')
            buf.seek(0)
            img_b64 = base64.b64encode(buf.read()).decode('UTF-8')
            _result = dict(
                figureCount=len(fignums),
                base64ImageString='data:image/png;base64,' + img_b64,
            )
        finally:
            try:
                plt.close('all')
                from matplotlib._pylab_helpers import Gcf
                Gcf.destroy_all()
            except Exception:
                pass

_result
`);

      const value =
        result !== undefined && typeof result === 'object' && 'toJs' in result
          ? (result as any).toJs({ dict_converter: Object.fromEntries })
          : result;
      if (result !== undefined && typeof result === 'object' && 'destroy' in result) {
        (result as any).destroy();
      }
      if (
        value !== undefined &&
        value !== null &&
        typeof value === 'object' &&
        'figureCount' in (value as any) &&
        typeof (value as any).figureCount === 'number'
      ) {
        return value as { base64ImageString?: string; figureCount: number };
      }
      return { figureCount: 0 };
    } catch {
      this.cleanupMatplotlib(pyodide);
      return { figureCount: 0 };
    }
  }

  async runExclusive<T>(fn: () => Promise<T>): Promise<T> {
    const previous = this.exclusiveRunQueue;
    let release: (() => void) | undefined;
    this.exclusiveRunQueue = new Promise<void>((resolve) => {
      release = resolve;
    });

    await previous;
    try {
      return await fn();
    } finally {
      release?.();
    }
  }

  static getInstance(): PyodideService {
    if (!PyodideService.instance) {
      PyodideService.instance = new PyodideService();
    }
    return PyodideService.instance;
  }

  async initializePyodide(): Promise<PyodideInterface> {
    if (this.pyodide) {
      return this.pyodide;
    }

    if (this.loading && this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loading = true;
    this.loadingPromise = this.loadPyodideAndModules();

    try {
      this.pyodide = await this.loadingPromise;
      return this.pyodide;
    } finally {
      this.loading = false;
      this.loadingPromise = null;
    }
  }

  private async loadPyodideAndModules(): Promise<PyodideInterface> {
    const pyodide = await loadPyodide({
      indexURL: `https://cdn.jsdelivr.net/pyodide/v${pyodide_version}/full/`,
    });

    this.configureMatplotlib(pyodide);

    // Default packages that are always loaded
    await pyodide.loadPackage(['pandas']);
    this.packageCache.standardPackages.add('pandas');

    return pyodide;
  }

  async ensurePackagesLoaded(
    pyCode: string,
    pypiPackages: string[],
    appendLog: (message: { type: 'out' | 'error'; string: string }) => void,
  ): Promise<void> {
    const pyodide = await this.initializePyodide();

    // Load packages from imports
    await pyodide.loadPackagesFromImports(pyCode);

    // Load pypi packages that haven't been loaded yet
    const uniquePypiPackages = [...new Set(pypiPackages)];
    const newPypiPackages = uniquePypiPackages.filter(
      (pkg) => !this.packageCache.pypiPackages.has(pkg),
    );

    if (newPypiPackages.length > 0) {
      if (!this.packageCache.standardPackages.has('micropip')) {
        await pyodide.loadPackage(['micropip']);
        this.packageCache.standardPackages.add('micropip');
      }

      const micropip = pyodide.pyimport('micropip');

      try {
        for (const pypiPackage of newPypiPackages) {
          appendLog({
            type: 'out',
            string: `Installing package: ${pypiPackage}`,
          });

          try {
            await micropip.install(pypiPackage);
            this.packageCache.pypiPackages.add(pypiPackage);
          } catch (error) {
            appendLog({
              type: 'error',
              string: `Failed to install package ${pypiPackage}: ${error}`,
            });
            throw error;
          }
        }
      } finally {
        if (micropip !== undefined && typeof micropip.destroy === 'function') {
          micropip.destroy();
        }
      }

      // Newly installed packages may include matplotlib; ensure backend and show() behavior are configured.
      this.configureMatplotlib(pyodide);
    }

    appendLog({
      type: 'out',
      string: 'Packages loaded',
    });
  }

  async executeCode(
    pyCode: string,
    context: PyodideExecutionContext,
    appendLog: (message: { type: 'out' | 'error'; string: string }) => void,
  ): Promise<any> {
    const pyodide = await this.initializePyodide();
    const { cellId } = context;

    pyodide.setStdout({
      batched: (str) => appendLog({ type: 'out', string: str }),
    });
    pyodide.setStderr({
      batched: (str) => appendLog({ type: 'error', string: str }),
    });

    let result: any;
    try {
      this.activeSupersimpleCellId = cellId;

      // Get or create an isolated namespace for this specific cell
      let namespace = this.cellNamespaces.get(cellId);

      if (namespace === undefined) {
        namespace = pyodide.runPython(`
          import sys
          import types
          import builtins

          # Create a new namespace with only built-ins and essential components
          cell_namespace = {
            '__name__': '__main__',
            '__doc__': None,
            '__package__': None,
            '__loader__': None,
            '__spec__': None,
            '__builtins__': builtins,
            '__cached__': None,
            '__file__': None,
          }

          # Add sys module for imports to work
          cell_namespace['sys'] = sys

          # Imports work via the interpreter's sys.modules; no need to copy modules into globals.

          cell_namespace
        `);

        this.cellNamespaces.set(cellId, namespace);
      }

      this.configureMatplotlib(pyodide);

      // Execute the code in the cell's isolated namespace
      result = await pyodide.runPythonAsync(pyCode, { globals: namespace });

      return { result, namespace };
    } catch (error) {
      // If user code raised before conversion/flushing, make sure we don't leak figures.
      this.cleanupMatplotlib(pyodide);
      throw error;
    } finally {
      pyodide.setStdout({ batched: () => {} });
      pyodide.setStderr({ batched: () => {} });
      if (this.activeSupersimpleCellId === cellId) {
        this.activeSupersimpleCellId = null;
      }
      // PyProxy cleanup is handled by the caller (convertPythonResult) since the result needs to be processed first
    }
  }

  async registerSupersimpleModule(
    cellId: string,
    loadPyData: (
      title: string,
      page?: boolean,
      limit?: number,
      page_size?: number,
      columns?: string[],
    ) => Promise<any>,
  ): Promise<void> {
    const pyodide = await this.initializePyodide();

    this.supersimpleLoadersByCellId.set(cellId, loadPyData);

    if (this.supersimpleModuleRegistered) {
      return;
    }

    pyodide.registerJsModule('supersimple', {
      getBlock: (title: string) => {
        // Python caches imports globally in sys.modules, so `supersimple` must not capture
        // cell-specific callbacks. Instead, we route calls based on the active cell id.
        const activeCellId = this.activeSupersimpleCellId;
        const activeLoadPyData =
          activeCellId !== null ? this.supersimpleLoadersByCellId.get(activeCellId) : undefined;
        if (activeCellId === null || activeLoadPyData === undefined) {
          throw new Error(
            'supersimple.getBlock() was called outside of an active Python cell execution context.',
          );
        }

        return {
          fetchAll: (
            keyword_args:
              | {
                  limit?: number;
                  page_size?: number;
                  columns?: any;
                }
              | undefined,
          ) => {
            const { limit, page_size, columns } = keyword_args ?? {};
            const load = activeLoadPyData(
              title,
              false,
              limit,
              page_size ?? 2000,
              columns?.toJs?.(),
            );

            return {
              as_json: async () => {
                return pyodide.toPy((await load)?.rows ?? []);
              },
              as_df: async () => {
                const data = await load;

                const columnTypes: { [key: string]: string } = (data?.fields ?? []).reduce(
                  (acc: any, field: any) => ({
                    ...acc,
                    [field.key]: this.convertToPythonType(field.type),
                  }),
                  {},
                );

                const columnKeyNames: { [key: string]: string } = (data?.fields ?? []).reduce(
                  (acc: any, field: any) => ({ ...acc, [field.key]: field.name }),
                  {},
                );

                return pyodide
                  .pyimport('pandas')
                  .DataFrame.callKwargs({ data: pyodide.toPy(data?.rows ?? []) })
                  .astype.callKwargs({ dtype: pyodide.toPy(columnTypes) })
                  .rename.callKwargs({ columns: pyodide.toPy(columnKeyNames) });
              },
            };
          },
        };
      },
    });
    this.supersimpleModuleRegistered = true;
  }

  async unregisterSupersimpleModule(cellId: string): Promise<void> {
    this.supersimpleLoadersByCellId.delete(cellId);
  }

  private convertToPythonType(type: string): string {
    switch (type) {
      case 'Integer':
        return 'int64';
      case 'Float':
        return 'float64';
      case 'Boolean':
        return 'bool';
      case 'DateTime':
        return 'datetime64[ns]';
      default:
        return 'object';
    }
  }

  reset(): void {
    // Clean up all cell namespaces
    this.cellNamespaces.forEach((namespace) => {
      if (namespace !== undefined && typeof namespace.destroy === 'function') {
        namespace.destroy();
      }
    });
    this.cellNamespaces.clear();

    this.pyodide = null;
    this.loading = false;
    this.supersimpleModuleRegistered = false;
    this.activeSupersimpleCellId = null;
    this.supersimpleLoadersByCellId.clear();
    this.packageCache = {
      pypiPackages: new Set(),
      standardPackages: new Set(),
    };
    this.loadingPromise = null;
  }

  // Method to clean up a specific cell's namespace (useful when a cell is deleted)
  clearCellNamespace(cellId: string): void {
    const namespace = this.cellNamespaces.get(cellId);
    if (namespace !== undefined && typeof namespace.destroy === 'function') {
      namespace.destroy();
    }
    this.cellNamespaces.delete(cellId);
  }

  getPackageCache(): PyodidePackageCache {
    return {
      pypiPackages: new Set(this.packageCache.pypiPackages),
      standardPackages: new Set(this.packageCache.standardPackages),
    };
  }
}

export const pyodideService = PyodideService.getInstance();
